<template>
  <div class="whatsapp-contact" :style="contactStyle">
    <div class="whatsapp-contact__row">
      <div class="whatsapp-contact__left">
        <div class="whatsapp-contact__image-container">
          <v-img v-if="contactImage" :src="contactImage" :alt="contact.name" class="whatsapp-contact__image" contain />
          <div v-else class="whatsapp-contact__image-placeholder">
            <v-icon size="60">store</v-icon>
          </div>
        </div>
        <div class="whatsapp-contact__content">
          <h3 class="whatsapp-contact__name" :style="{ color: getAssets.colors.whatsapp.contactCardTitle }">
            {{ contact.name }}
          </h3>
          <p v-if="contact.description" class="whatsapp-contact__address" :style="{ color: getAssets.colors.whatsapp.contactCardDescription }">
            {{ contact.description }}
          </p>
        </div>
      </div>
      <div class="whatsapp-contact__right">
        <WhatsappContactCardActionDesktop v-if="isDesktopActions" :whatsapp-link="whatsappLink" :is-community="isCommunityBot" />
        <WhatsappContactCardActionMobile v-else :whatsapp-link="whatsappLink" :is-community="isCommunityBot" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { screenTypeMixin } from '@/mixins/screen-type'
import { buildWhatsAppUrl } from '@/helpers/whatsapp'
import WhatsappContactCardActionDesktop from './WhatsappContactCardActionDesktop.vue'
import WhatsappContactCardActionMobile from './WhatsappContactCardActionMobile.vue'

export default {
  name: 'WhatsAppContactCard',
  components: {
    WhatsappContactCardActionDesktop,
    WhatsappContactCardActionMobile
  },
  mixins: [screenTypeMixin],
  props: {
    contact: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters(['GET_ASSETS']),
    getAssets() {
      return this.GET_ASSETS
    },
    contactStyle() {
      return {
        'background-color': this.getAssets.colors.whatsapp.contactCardBackground
      }
    },
    whatsappLink() {
      return buildWhatsAppUrl(this.contact)
    },
    contactImage() {
      // Use contact image if available, otherwise fallback to finish logo
      return this.contact.image || this.getAssets.images?.finish?.logo || null
    },
    isCommunityBot() {
      return !!(this.contact.is_community_redirect && this.contact.community_link)
    },
    isDesktopActions() {
      return this.screenType === 'desktop'
    }
  }
}
</script>

<style lang="scss" scoped>
.whatsapp-contact {
  display: flex;
  flex-direction: column;
  padding: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12), 0 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  width: 100%;
  max-width: 900px;

  &__row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 16px;
    width: 100%;
    min-width: 0;
  }

  &__left {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 16px;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__right {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
    min-height: 0;
  }

  &__image-container {
    flex-shrink: 0;
    width: 160px;
    aspect-ratio: 2 / 1.5;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
  }

  &__image {
    width: 100%;
    height: 100%;
    ::v-deep .v-image__image {
      object-fit: contain !important;
      object-position: center;
    }
  }

  &__image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
    color: #999;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 8px;
    line-height: 1.2;
  }

  &__address {
    font-size: 14px;
    opacity: 0.7;
    margin: 0;
    line-height: 1.4;
  }
}

/* Below desktop (screenType mixin): same viewport where QR is hidden — stack image+text, actions full-width under */
@media screen and (max-width: 1024px) {
  .whatsapp-contact {
    &__row {
      flex-direction: column;
      align-items: stretch;
      gap: 14px;
    }

    &__left {
      width: 100%;
      flex: 0 0 auto;
    }

    &__right {
      width: 100%;
      flex: 0 0 auto;
      justify-content: stretch;
      align-items: stretch;
    }
  }
}

@media screen and (max-width: 960px) {
  .whatsapp-contact {
    padding: 14px;

    &__row,
    &__left {
      gap: 12px;
    }

    &__image-container {
      width: 120px;
      aspect-ratio: 2 / 1.5;
    }

    &__name {
      font-size: 20px;
    }

    &__address {
      font-size: 13px;
    }
  }
}

@media screen and (max-width: 600px) {
  .whatsapp-contact {
    padding: 12px;

    &__row,
    &__left {
      gap: 10px;
    }

    &__image-container {
      width: 72px;
      aspect-ratio: 2 / 1.5;
    }

    &__name {
      font-size: 16px;
    }

    &__address {
      font-size: 12px;
    }
  }
}
</style>
